import { Link } from "react-router";
import type { AudienceScope, AudienceWarning, EffectiveAudience } from "@/shared/contracts";

/**
 * 设置页「现在家人以外谁能看到」（PRIVACY-COPY-01；c84a REQ15 第 3 节，I 10-09 采用；S-055、R14-UX07、KEEP-7／G-09）。
 * - 只照后端 effective_audience 写（PRIV-AUDIENCE-01b，app/web_social/audience.py：逐项和真实读取端用同一个判定），前端不拿两个开关自己推算。
 * - 主页、名字和物种、照片、到访动态四项分开说，每项写此刻家人以外谁能看到；家人永远看得到自家的全部，所以只说家人以外。
 * - 三种提醒照后端 warnings 写：动态比主页开得宽（主页没公开、动态却公开，最显眼）、谁都能关注（「仅关注者」不是经你同意的圈子）、照片只给家人。
 * - 直达 TA 的主页看已经公开的动态，主人在那里能撤下（c84a：设置页可直达已发布动态）。
 * 后端没给（老后端、没有宠物）时整块不出。
 */
const SCOPE: Record<AudienceScope, string> = {
  everyone: "所有人，不登录也能看",
  signed_in: "登录了的人",
  followers: "关注了 TA 的宠物的家人",
  family: "只有家人",
};

const WARNING: Record<AudienceWarning, string> = {
  posts_wider_than_profile:
    "TA 的主页没有公开，可到访动态是公开的：不登录的人也能在星球上看到 TA 的名字、物种和动态。不想公开，就关掉上面的开关；已经发出去的，可以到 TA 的主页撤下。",
  followers_open_to_anyone: "关注不用你同意：谁都能让自家宠物关注 TA，关注以后就能看到 TA 的主页。",
  photo_family_only: "TA 的照片只给家人看，别人看到的是头像占位。",
};

const known = (w: string): w is AudienceWarning => Object.prototype.hasOwnProperty.call(WARNING, w);

export function AudienceSummary({ audience, petId }: { audience: EffectiveAudience | null | undefined; petId: string | null }) {
  if (!audience) return null;
  const rows: Array<[string, AudienceScope]> = [
    ["TA 的主页", audience.profile_page],
    ["名字和物种", audience.profile_card],
  ];
  if (audience.photo) rows.push(["TA 的照片", audience.photo]);
  rows.push(["到访动态", audience.posts]);
  const warnings = (audience.warnings ?? []).filter(known);
  return (
    <section className="ps-settings-audience" aria-labelledby="settings-audience-title" data-testid="settings-audience">
      <p id="settings-audience-title" className="ps-settings-audience__title">
        现在家人以外谁能看到
      </p>
      <dl className="ps-settings-audience__list">
        {rows.map(([what, scope]) => (
          <div key={what} className="ps-settings-audience__row" data-scope={scope}>
            <dt>{what}</dt>
            <dd>{SCOPE[scope]}</dd>
          </div>
        ))}
      </dl>
      {warnings.includes("posts_wider_than_profile") ? (
        <p className="ps-settings-audience__warn" role="note">
          {WARNING.posts_wider_than_profile}
        </p>
      ) : null}
      {warnings
        .filter((w) => w !== "posts_wider_than_profile")
        .map((w) => (
          <p key={w} className="ps-settings-audience__note">
            {WARNING[w]}
          </p>
        ))}
      {petId ? (
        <Link className="ps-settings-audience__posts" to={`/pets/${encodeURIComponent(petId)}`}>
          看看 TA 已经公开的动态
        </Link>
      ) : null}
    </section>
  );
}
